import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ChevronRight, ChevronDown, Package, TrendingUp, ShoppingCart, Scale, Download,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import DateFilter from "@/components/DateFilter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODES = [
  { value: "margin", label: "Margin Analysis", icon: Scale, desc: "Sale vs Purchase with Gross Margin" },
  { value: "sale", label: "Stock-wise Sales", icon: TrendingUp, desc: "Monthly pivot by item + party drill-down" },
  { value: "purchase", label: "Stock-wise Purchase", icon: ShoppingCart, desc: "Monthly pivot by item + party drill-down" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "no_movement", label: "No Movement" },
  { value: "sold_only", label: "Sold Only" },
  { value: "purchased_only", label: "Purchased Only" },
];

const STATUS_BADGE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sold_only: "bg-blue-50 text-blue-700 border-blue-200",
  purchased_only: "bg-amber-50 text-amber-700 border-amber-200",
  no_movement: "bg-rose-50 text-rose-700 border-rose-200",
};

const STATUS_LABEL = {
  active: "Active",
  sold_only: "Sold only",
  purchased_only: "Purchased only",
  no_movement: "No movement",
};

function fmt(n) {
  if (n === null || n === undefined) return "—";
  if (n === 0) return "—";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

function fmtPct(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return `${n.toFixed(1)}%`;
}

function fmtMonth(m) {
  const [y, mm] = m.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(mm, 10) - 1]}-${y.slice(2)}`;
}

function Badge({ status }) {
  const cls = STATUS_BADGE[status] || STATUS_BADGE.no_movement;
  return (
    <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

/** Margin Analysis row (side-by-side Sale/Purchase) */
function MarginRow({ row }) {
  return (
    <TableRow
      className={row.status === "no_movement" ? "opacity-60 hover:bg-slate-50" : "hover:bg-slate-50"}
      data-testid={`margin-row-${(row.item || "").replace(/\s+/g, "-").toLowerCase()}`}
    >
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <span>{row.item}</span>
          <Badge status={row.status} />
        </div>
      </TableCell>
      <TableCell className="text-xs text-slate-500">{row.unit || "—"}</TableCell>
      <TableCell className="text-right font-mono tabular-nums text-xs">{fmt(row.sale_qty)}</TableCell>
      <TableCell className="text-right font-mono tabular-nums text-xs">{fmt(row.avg_sale_rate)}</TableCell>
      <TableCell className="text-right font-mono tabular-nums">{fmt(row.sale_amt)}</TableCell>
      <TableCell className="text-right font-mono tabular-nums text-xs">{fmt(row.pur_qty)}</TableCell>
      <TableCell className="text-right font-mono tabular-nums text-xs">{fmt(row.avg_pur_rate)}</TableCell>
      <TableCell className="text-right font-mono tabular-nums">{fmt(row.pur_amt)}</TableCell>
      <TableCell
        className={`text-right font-mono tabular-nums font-semibold ${
          row.gross_profit >= 0 ? "text-emerald-700" : "text-rose-700"
        }`}
      >
        {fmt(row.gross_profit)}
      </TableCell>
      <TableCell
        className={`text-right font-mono tabular-nums text-xs ${
          row.margin_pct >= 0 ? "text-emerald-700" : "text-rose-700"
        }`}
      >
        {row.avg_pur_rate > 0 ? fmtPct(row.margin_pct) : "—"}
      </TableCell>
      <TableCell className="text-right font-mono tabular-nums text-xs text-slate-600">
        {row.net_qty !== 0 ? fmt(row.net_qty) : "—"}
      </TableCell>
    </TableRow>
  );
}

/** Stock-wise Sales / Purchase — pivot row with monthly cols and party drill-down */
function ItemPivotRow({ row, months }) {
  const [expanded, setExpanded] = useState(false);
  const hasParties = row.parties && row.parties.length > 0;
  return (
    <>
      <TableRow
        className={`cursor-pointer hover:bg-slate-50 ${row.qty === 0 ? "opacity-60" : ""}`}
        onClick={() => hasParties && setExpanded(!expanded)}
        data-testid={`item-pivot-row-${(row.item || "").replace(/\s+/g, "-").toLowerCase()}`}
      >
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            {hasParties ? (
              expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />
            ) : (<span className="w-4" />)}
            <span>{row.item}</span>
            {row.qty === 0 && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200">
                No Movement
              </span>
            )}
          </div>
        </TableCell>
        <TableCell className="text-xs text-slate-500">{row.unit || "—"}</TableCell>
        <TableCell className="text-right font-mono tabular-nums text-xs">{fmt(row.qty)}</TableCell>
        <TableCell className="text-right font-mono tabular-nums text-xs">{fmt(row.avg_rate)}</TableCell>
        {months.map((m) => (
          <TableCell key={m} className="text-right font-mono tabular-nums text-xs">
            {row.monthly[m] > 0 ? fmt(row.monthly[m]) : "—"}
          </TableCell>
        ))}
        <TableCell className="text-right font-mono tabular-nums font-semibold text-slate-900">
          {fmt(row.amount)}
        </TableCell>
      </TableRow>
      {expanded && hasParties && (
        <TableRow className="bg-slate-50">
          <TableCell colSpan={months.length + 5} className="p-0">
            <div className="p-4 pl-10">
              <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">
                Party-wise Breakdown
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500">
                    <th className="text-left font-medium py-1">Party</th>
                    <th className="text-right font-medium py-1">Qty</th>
                    <th className="text-right font-medium py-1">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {row.parties.map((p) => (
                    <tr key={p.party}>
                      <td className="py-1">{p.party}</td>
                      <td className="text-right font-mono tabular-nums py-1">{fmt(p.qty)}</td>
                      <td className="text-right font-mono tabular-nums py-1">{fmt(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function StockMovement({ companyContext, initialMode = "margin" }) {
  const [mode, setMode] = useState(initialMode);
  const [includeAll, setIncludeAll] = useState(false);
  const [status, setStatus] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [downloading, setDownloading] = useState(false);

  const companyId = companyContext?.companyId || "default";
  const branchId = companyContext?.branchId || null;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url, params;
      if (mode === "margin") {
        url = `${API}/reports/item-movement`;
        params = {
          ...filters, mode, company_id: companyId,
          include_all_items: includeAll, status,
        };
      } else {
        url = `${API}/reports/item-pivot`;
        params = {
          ...filters, mode, company_id: companyId,
          include_all_items: includeAll,
        };
      }
      if (branchId) params.branch_id = branchId;
      const res = await axios.get(url, { params });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [filters, mode, companyId, branchId, includeAll, status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      let url, params;
      if (mode === "margin") {
        url = `${API}/reports/item-movement/export`;
        params = {
          ...filters, mode, company_id: companyId,
          include_all_items: includeAll,
        };
      } else {
        url = `${API}/reports/item-pivot/export`;
        params = {
          ...filters, mode, company_id: companyId,
          include_all_items: includeAll,
        };
      }
      if (branchId) params.branch_id = branchId;
      const res = await axios.get(url, { params, responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = u;
      a.download = `Stock_${mode}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Download failed: " + e.message);
    } finally {
      setDownloading(false);
    }
  };

  // Normalized data
  const marginRows = mode === "margin" ? (data?.rows || []) : [];
  const marginTotals = mode === "margin" ? (data?.totals || {}) : {};
  const pivotItems = mode !== "margin" ? (data?.items || []) : [];
  const pivotMonths = mode !== "margin" ? (data?.months || []) : [];
  const pivotMonthlyTotals = mode !== "margin" ? (data?.monthly_totals || {}) : {};
  const pivotGrandTotal = mode !== "margin" ? (data?.grand_total || 0) : 0;
  const pivotTotalQty = mode !== "margin" ? (data?.total_qty || 0) : 0;

  return (
    <div data-testid="stock-movement">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Stock Item-wise Report</h1>
          <p className="text-sm text-slate-500 mt-1">
            Sales, Purchase & Margin analysis per item. Stock-wise Sales &amp; Purchase use monthly pivot format.
          </p>
        </div>
        <Button
          onClick={handleDownload}
          disabled={downloading}
          variant="outline"
          className="border-slate-300"
          data-testid="stock-movement-download"
        >
          <Download className="w-4 h-4 mr-2" />
          {downloading ? "Exporting..." : "Download Excel"}
        </Button>
      </div>

      {/* Mode selector */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.value;
          return (
            <button
              key={m.value}
              onClick={() => setMode(m.value)}
              data-testid={`mode-${m.value}`}
              className={`text-left rounded-md border px-4 py-3 transition-all ${
                active
                  ? "border-slate-900 bg-slate-900 text-white shadow-md"
                  : "border-slate-200 bg-white hover:border-slate-400"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${active ? "text-white" : "text-slate-600"}`} />
                <span className="text-sm font-semibold">{m.label}</span>
              </div>
              <p className={`text-[11px] ${active ? "text-white/80" : "text-slate-500"}`}>{m.desc}</p>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <DateFilter onFilterChange={setFilters} />
        <label
          className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer bg-white border border-slate-200 rounded-md px-3 py-1.5"
          data-testid="toggle-include-all"
        >
          <input
            type="checkbox"
            checked={includeAll}
            onChange={(e) => setIncludeAll(e.target.checked)}
            className="rounded border-slate-300"
          />
          <span className="font-medium">Include items with no movement</span>
        </label>

        {mode === "margin" && (
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden bg-white">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.value}
                onClick={() => setStatus(s.value)}
                data-testid={`status-${s.value}`}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  status === s.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Totals */}
      {mode === "margin" ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Card className="border-slate-200 shadow-none"><CardContent className="p-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase">Items Shown</p>
            <p className="text-lg font-bold font-mono">{data?.count || 0}</p>
          </CardContent></Card>
          <Card className="border-slate-200 shadow-none"><CardContent className="p-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase">Total Sale</p>
            <p className="text-lg font-bold font-mono text-emerald-700">{fmt(marginTotals.total_sale_amt)}</p>
          </CardContent></Card>
          <Card className="border-slate-200 shadow-none"><CardContent className="p-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase">Total Purchase</p>
            <p className="text-lg font-bold font-mono text-amber-700">{fmt(marginTotals.total_pur_amt)}</p>
          </CardContent></Card>
          <Card className="border-slate-200 shadow-none"><CardContent className="p-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase">Gross Profit</p>
            <p className={`text-lg font-bold font-mono ${
              marginTotals.total_gross_profit >= 0 ? "text-emerald-700" : "text-rose-700"
            }`}>{fmt(marginTotals.total_gross_profit)}</p>
          </CardContent></Card>
          <Card className="border-slate-200 shadow-none"><CardContent className="p-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase">No Movement</p>
            <p className="text-lg font-bold font-mono text-rose-700">{marginTotals.no_movement_count || 0}</p>
          </CardContent></Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <Card className="border-slate-200 shadow-none"><CardContent className="p-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase">Items</p>
            <p className="text-lg font-bold font-mono">{pivotItems.length}</p>
          </CardContent></Card>
          <Card className="border-slate-200 shadow-none"><CardContent className="p-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase">Total Qty</p>
            <p className="text-lg font-bold font-mono">{fmt(pivotTotalQty)}</p>
          </CardContent></Card>
          <Card className="border-slate-200 shadow-none"><CardContent className="p-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase">Total Amount</p>
            <p className={`text-lg font-bold font-mono ${mode === "sale" ? "text-emerald-700" : "text-amber-700"}`}>
              {fmt(pivotGrandTotal)}
            </p>
          </CardContent></Card>
        </div>
      )}

      <Card className="border border-slate-200 shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Package className="w-4 h-4" />
            {MODES.find((m) => m.value === mode)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="spinner"></div></div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : mode === "margin" ? (
            marginRows.length === 0 ? (
              <div className="text-center text-slate-500 py-8">No data for this filter</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Item</TableHead>
                    <TableHead className="text-xs font-semibold">Unit</TableHead>
                    <TableHead className="text-right text-xs font-semibold">Sale Qty</TableHead>
                    <TableHead className="text-right text-xs font-semibold">Avg Sale ₹</TableHead>
                    <TableHead className="text-right text-xs font-semibold">Sale Amt</TableHead>
                    <TableHead className="text-right text-xs font-semibold">Pur Qty</TableHead>
                    <TableHead className="text-right text-xs font-semibold">Avg Pur ₹</TableHead>
                    <TableHead className="text-right text-xs font-semibold">Pur Amt</TableHead>
                    <TableHead className="text-right text-xs font-semibold">Gross Profit</TableHead>
                    <TableHead className="text-right text-xs font-semibold">Margin %</TableHead>
                    <TableHead className="text-right text-xs font-semibold">Net Qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {marginRows.map((r) => <MarginRow key={r.item} row={r} />)}
                </TableBody>
              </Table>
            )
          ) : pivotItems.length === 0 ? (
            <div className="text-center text-slate-500 py-8">No data for this filter</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Item</TableHead>
                  <TableHead className="text-xs font-semibold">Unit</TableHead>
                  <TableHead className="text-right text-xs font-semibold">Total Qty</TableHead>
                  <TableHead className="text-right text-xs font-semibold">Avg Rate</TableHead>
                  {pivotMonths.map((m) => (
                    <TableHead key={m} className="text-right text-xs font-semibold w-28">
                      {fmtMonth(m)}
                    </TableHead>
                  ))}
                  <TableHead className="text-right font-semibold w-36 bg-slate-100">Total (₹)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pivotItems.map((r) => <ItemPivotRow key={r.item} row={r} months={pivotMonths} />)}
                <TableRow className="bg-slate-100 font-bold">
                  <TableCell>Total</TableCell>
                  <TableCell />
                  <TableCell className="text-right font-mono tabular-nums text-xs">{fmt(pivotTotalQty)}</TableCell>
                  <TableCell />
                  {pivotMonths.map((m) => (
                    <TableCell key={m} className="text-right font-mono tabular-nums text-xs">
                      {fmt(pivotMonthlyTotals[m] || 0)}
                    </TableCell>
                  ))}
                  <TableCell className={`text-right font-mono tabular-nums ${mode === "sale" ? "text-emerald-700" : "text-amber-700"}`}>
                    {fmt(pivotGrandTotal)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
